import React, { useEffect, useRef, useState } from 'react';
import { DOCUMENTOS_ALUNO, baixarDocumentoAluno } from '../services/documentosAluno';

/**
 * Botão "Documentos" com a lista de documentos do aluno para baixar em PDF.
 * Usado no Perfil do Aluno.
 */
export function MenuDeDocumentos({ aluno, darkMode, documentos = DOCUMENTOS_ALUNO }) {
  const [aberto, setAberto] = useState(false);
  const [gerando, setGerando] = useState(null);
  const [erro, setErro] = useState(null);
  const caixa = useRef(null);

  useEffect(() => {
    if (!aberto) return undefined;
    const fora = (e) => { if (caixa.current && !caixa.current.contains(e.target)) setAberto(false); };
    document.addEventListener('mousedown', fora);
    return () => document.removeEventListener('mousedown', fora);
  }, [aberto]);

  const baixar = async (tipo) => {
    setGerando(tipo);
    setErro(null);
    try {
      await baixarDocumentoAluno(aluno, tipo);
      setAberto(false);
    } catch (err) {
      setErro(err.message);
    } finally {
      setGerando(null);
    }
  };

  return (
    <div ref={caixa} className="relative">
      <button
        type="button"
        onClick={() => setAberto((v) => !v)}
        aria-expanded={aberto}
        className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-[#0974BA] hover:bg-[#0974BA]/85 text-white transition-colors"
      >
        <span aria-hidden="true">📄</span> Documentos <span aria-hidden="true">{aberto ? '▴' : '▾'}</span>
      </button>
      {aberto && (
        <div className={`absolute right-0 mt-2 w-80 rounded-xl border shadow-lg z-40 p-1.5 ${
          darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'
        }`}>
          {documentos.map((d) => (
            <button
              key={d.tipo}
              type="button"
              onClick={() => baixar(d.tipo)}
              disabled={gerando !== null}
              className={`w-full text-left px-3 py-2 rounded-lg transition-colors disabled:opacity-60 ${
                darkMode ? 'hover:bg-slate-700' : 'hover:bg-slate-50'
              }`}
            >
              <span className={`block text-sm font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                {gerando === d.tipo ? 'Gerando...' : d.rotulo}
              </span>
              <span className={`block text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>{d.apoio}</span>
            </button>
          ))}
          {erro && <p className="px-3 py-2 text-xs text-[#E83A3C]">{erro}</p>}
        </div>
      )}
    </div>
  );
}

/** Os mesmos documentos como uma fileira de botões (Relatórios). */
export function BotoesDeDocumentos({ aluno, darkMode, onErro, documentos = DOCUMENTOS_ALUNO }) {
  const [gerando, setGerando] = useState(null);

  const baixar = async (tipo) => {
    setGerando(tipo);
    onErro?.(null);
    try {
      await baixarDocumentoAluno(aluno, tipo);
    } catch (err) {
      onErro?.(err.message);
    } finally {
      setGerando(null);
    }
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
      {documentos.map((d) => (
        <button
          key={d.tipo}
          type="button"
          onClick={() => baixar(d.tipo)}
          disabled={!aluno || gerando !== null}
          title={aluno ? `Baixar ${d.rotulo} de ${aluno.student_name}` : 'Escolha o aluno'}
          className={`text-left p-3 rounded-lg border transition-colors disabled:opacity-50 ${
            darkMode
              ? 'border-slate-700 hover:border-[#0974BA]/60 bg-slate-900/30'
              : 'border-slate-200 hover:border-[#0974BA]/50 bg-white'
          }`}
        >
          <span className={`block text-sm font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
            {gerando === d.tipo ? 'Gerando...' : d.rotulo}
          </span>
          <span className={`block text-xs mt-0.5 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>{d.apoio}</span>
        </button>
      ))}
    </div>
  );
}
